import { useCallback, useEffect, useRef, useState } from 'react'

import {
    clickArrowLeft,
    clickArrowRight,
    Config,
    defaultArrowLeft,
    defaultArrowRight,
    defaultConfig,
    getCarouselLeftArrow,
    getCarouselRightArrow,
    resetCarouselsArrows
} from '@lib/utils/carousel'
import { elementIsOnScreen } from '@lib/utils/DOM'

const useCarousel = (isValidating: boolean, config?: Config): void => {
    const [hasReachedStart, setHasReachedStart] = useState(true)
    const [hasReachedEnd, setHasReachedEnd] = useState(true)

    const slideClickCounter = useRef(0)

    const {
        selector,
        isMobile,
        slideSelector,
        arrowLeftCustomComponent = defaultArrowLeft,
        arrowRightCustomComponent = defaultArrowRight,
        revalidate
    } = config || defaultConfig

    const onCarouselScroll = useCallback((event: Event) => {
        const carousel = event.target as HTMLElement

        if (carousel.scrollLeft === 0) {
            setHasReachedStart(true)
            return
        }

        if (
            Math.trunc(carousel.offsetWidth + carousel.scrollLeft + 1) >=
            Math.trunc(carousel.scrollWidth)
        ) {
            setHasReachedEnd(true)
            return
        }

        setHasReachedStart(false)
        setHasReachedEnd(false)
    }, [])

    const onSlideClick = useCallback(() => {
        const rightArrowSvg = document.querySelector<HTMLElement>(
            '.carousel-arrow-right svg'
        )

        if (rightArrowSvg && slideClickCounter.current === 0) {
            rightArrowSvg.style.display = 'inline-block'
            slideClickCounter.current++
        }
    }, [])

    const assertOnCarouselScroll = useCallback(() => {
        const carousel = document.querySelector<HTMLElement>(selector)
        if (!carousel) return
        setHasReachedEnd(carousel.offsetWidth === carousel.scrollWidth)
    }, [selector])

    useEffect(() => {
        setHasReachedStart(true)
        setHasReachedEnd(false)
    }, [revalidate])

    useEffect(() => {
        assertOnCarouselScroll()
        window.addEventListener('resize', assertOnCarouselScroll)

        return () =>
            window.removeEventListener('resize', assertOnCarouselScroll)
    }, [assertOnCarouselScroll, isValidating, selector])

    useEffect(() => {
        const carouselElement = document.querySelector<HTMLElement>(selector)
        const slideElement = document.querySelector<HTMLElement>(slideSelector)
        const slideElements =
            document.querySelectorAll<HTMLElement>(slideSelector)

        if (!carouselElement || !slideElement) return

        carouselElement.addEventListener('scroll', onCarouselScroll)

        // for safari only
        slideElements.forEach((slide) =>
            slide.addEventListener('click', onSlideClick)
        )

        elementIsOnScreen('.slick-slider', () => {
            const slickRightArrow =
                document.querySelector<HTMLElement>('.slick-next')

            const slickLeftArrow =
                document.querySelector<HTMLElement>('.slick-prev')

            if (slickRightArrow && slickLeftArrow) {
                slickLeftArrow.innerHTML = arrowLeftCustomComponent
                slickRightArrow.innerHTML = arrowRightCustomComponent
            }
        })

        //Only mobile for now
        elementIsOnScreen(selector, () => {
            if (carouselElement && slideElement && isMobile) {
                resetCarouselsArrows(carouselElement)

                carouselElement.classList.add('relative')

                const leftArrow = getCarouselLeftArrow(hasReachedStart, config)
                const rightArrow = getCarouselRightArrow(hasReachedEnd, config)

                rightArrow.onclick = () =>
                    clickArrowRight(carouselElement, slideElement)
                leftArrow.onclick = () =>
                    clickArrowLeft(carouselElement, slideElement)

                carouselElement.append(leftArrow, rightArrow)
            } else {
                resetCarouselsArrows(carouselElement)
            }
        })

        return () => {
            carouselElement.removeEventListener('scroll', onCarouselScroll)
            slideElements.forEach((slide) =>
                slide.removeEventListener('click', onSlideClick)
            )
        }
    }, [
        isValidating,
        config?.isMobile,
        hasReachedStart,
        hasReachedEnd,
        selector,
        revalidate
    ])
}

export default useCarousel
